import type { GrammarConfig, Question } from './topics';
import { options, pick, shuffle } from './util';

// ─────────────────────────────────────────────────────────────────────────────
// Portuguese (Brazil), by rule. Você / vocês take the 3rd-person forms, which
// is how Brazil speaks; "tu" is included only if the teacher ticks it (the
// same switch Spanish classes use for vosotros).
// ─────────────────────────────────────────────────────────────────────────────

type P = { labels: string[]; i: number };
const PEOPLE: P[] = [
  { labels: ['eu'], i: 0 },
  { labels: ['tu'], i: 1 },
  { labels: ['você', 'ele', 'ela'], i: 2 },
  { labels: ['nós'], i: 3 },
  { labels: ['vocês', 'eles', 'elas'], i: 4 },
];
const ENDINGS: Record<'ar' | 'er' | 'ir', string[]> = {
  ar: ['o', 'as', 'a', 'amos', 'am'],
  er: ['o', 'es', 'e', 'emos', 'em'],
  ir: ['o', 'es', 'e', 'imos', 'em'],
};

export const PT_VERBS: Record<string, string> = {
  falar: 'to speak', morar: 'to live', trabalhar: 'to work', estudar: 'to study', cantar: 'to sing',
  dançar: 'to dance', comprar: 'to buy', andar: 'to walk', tocar: 'to play (music) / touch', jogar: 'to play (a game)',
  chegar: 'to arrive', gostar: 'to like', comer: 'to eat', beber: 'to drink', aprender: 'to learn',
  vender: 'to sell', correr: 'to run', escrever: 'to write', viver: 'to live', abrir: 'to open',
  partir: 'to leave', decidir: 'to decide', assistir: 'to watch', dividir: 'to share',
};

export function ptPresent(verb: string, i: number): string {
  const type = verb.slice(-2) as 'ar' | 'er' | 'ir';
  return verb.slice(0, -2) + ENDINGS[type][i];
}

export function ptRegular(cfg: GrammarConfig, n: number): Question[] {
  const people = cfg.vosotros ? PEOPLE : PEOPLE.filter(p => p.i !== 1);
  const verbs = Object.keys(PT_VERBS);
  const qs: Question[] = [];
  for (let k = 0; k < n; k++) {
    const verb = pick(verbs);
    const p = pick(people);
    const answer = ptPresent(verb, p.i);
    const typed = Math.random() < 0.5;
    qs.push({
      key: `pt_present:${verb}:${p.i}`, topic: 'pt_present', kind: typed ? 'type' : 'choice',
      instruction: typed ? 'Present tense · write the verb' : 'Present tense · choose the verb',
      prompt: `${pick(p.labels)} ___ (${verb})`, hint: PT_VERBS[verb], answers: [answer],
      options: options(answer, people.map(q => ptPresent(verb, q.i))),
    });
  }
  return qs;
}

type SE = { s: string; ser: string; estar: string; use: 'ser' | 'estar'; en: string; why: string };
const PT_SER_ESTAR: SE[] = [
  { s: 'Eu ___ professor.', ser: 'sou', estar: 'estou', use: 'ser', en: 'I am a teacher.', why: 'Profession → ser' },
  { s: 'Nós ___ do Brasil.', ser: 'somos', estar: 'estamos', use: 'ser', en: 'We are from Brazil.', why: 'Origin → ser' },
  { s: 'Ela ___ alta.', ser: 'é', estar: 'está', use: 'ser', en: 'She is tall.', why: 'Description → ser' },
  { s: 'Vocês ___ meus amigos.', ser: 'são', estar: 'estão', use: 'ser', en: 'You are my friends.', why: 'Relationship → ser' },
  { s: 'Hoje ___ segunda-feira.', ser: 'é', estar: 'está', use: 'ser', en: 'Today is Monday.', why: 'Days → ser' },
  { s: '___ três horas.', ser: 'São', estar: 'Estão', use: 'ser', en: 'It is three o’clock.', why: 'Time → ser' },
  { s: 'A mesa ___ de madeira.', ser: 'é', estar: 'está', use: 'ser', en: 'The table is made of wood.', why: 'Material → ser' },
  { s: 'O livro ___ da Ana.', ser: 'é', estar: 'está', use: 'ser', en: 'The book is Ana’s.', why: 'Possession → ser' },
  { s: 'Você ___ muito inteligente.', ser: 'é', estar: 'está', use: 'ser', en: 'You are very smart.', why: 'Characteristic → ser' },
  { s: 'A festa ___ no sábado.', ser: 'é', estar: 'está', use: 'ser', en: 'The party is on Saturday.', why: 'When an event happens → ser' },
  { s: 'Eu ___ cansado hoje.', ser: 'sou', estar: 'estou', use: 'estar', en: 'I am tired today.', why: 'Condition → estar' },
  { s: 'Ela ___ doente.', ser: 'é', estar: 'está', use: 'estar', en: 'She is sick.', why: 'Health → estar' },
  { s: 'Nós ___ na escola.', ser: 'somos', estar: 'estamos', use: 'estar', en: 'We are at school.', why: 'Where someone is → estar' },
  { s: 'Onde ___ as chaves?', ser: 'são', estar: 'estão', use: 'estar', en: 'Where are the keys?', why: 'Where something is → estar' },
  { s: 'O café ___ frio.', ser: 'é', estar: 'está', use: 'estar', en: 'The coffee is cold.', why: 'Condition right now → estar' },
  { s: 'A porta ___ aberta.', ser: 'é', estar: 'está', use: 'estar', en: 'The door is open.', why: 'Condition → estar' },
  { s: 'Eles ___ estudando.', ser: 'são', estar: 'estão', use: 'estar', en: 'They are studying.', why: 'Action in progress (-ando/-endo) → estar' },
  { s: 'Você ___ feliz hoje?', ser: 'é', estar: 'está', use: 'estar', en: 'Are you happy today?', why: 'Feeling → estar' },
  { s: 'Eu ___ com fome.', ser: 'sou', estar: 'estou', use: 'estar', en: 'I am hungry.', why: 'Estar com fome = to be hungry' },
  { s: 'O céu ___ nublado.', ser: 'é', estar: 'está', use: 'estar', en: 'The sky is cloudy.', why: 'Condition → estar' },
  { s: 'Meu pai ___ em casa.', ser: 'é', estar: 'está', use: 'estar', en: 'My dad is at home.', why: 'Where someone is → estar' },
];

export function ptSerEstar(n: number): Question[] {
  const qs: Question[] = [];
  for (let k = 0; k < n; k++) {
    const x = pick(PT_SER_ESTAR);
    const answer = x.use === 'ser' ? x.ser : x.estar;
    const typed = Math.random() < 0.3;
    qs.push({
      key: `pt_ser_estar:${x.s}`, topic: 'pt_ser_estar', kind: typed ? 'type' : 'choice',
      instruction: typed ? 'Write ser or estar, conjugated' : 'Ser or estar?',
      prompt: x.s, hint: x.en, why: x.why, answers: [answer],
      options: shuffle([x.ser, x.estar]),
    });
  }
  return qs;
}

const PT_NOUNS: { w: string; g: 'm' | 'f'; en: string; trap?: boolean }[] = [
  { w: 'livro', g: 'm', en: 'book' }, { w: 'casa', g: 'f', en: 'house' }, { w: 'carro', g: 'm', en: 'car' },
  { w: 'mesa', g: 'f', en: 'table' }, { w: 'cachorro', g: 'm', en: 'dog' }, { w: 'escola', g: 'f', en: 'school' },
  { w: 'cidade', g: 'f', en: 'city' }, { w: 'pão', g: 'm', en: 'bread' }, { w: 'árvore', g: 'f', en: 'tree' },
  { w: 'amor', g: 'm', en: 'love' }, { w: 'flor', g: 'f', en: 'flower' }, { w: 'mar', g: 'm', en: 'sea' },
  { w: 'coração', g: 'm', en: 'heart' }, { w: 'lição', g: 'f', en: 'lesson' }, { w: 'televisão', g: 'f', en: 'television' },
  { w: 'leite', g: 'm', en: 'milk' }, { w: 'ponte', g: 'f', en: 'bridge' }, { w: 'sofá', g: 'm', en: 'sofa' },
  { w: 'viagem', g: 'f', en: 'trip', trap: true }, { w: 'garagem', g: 'f', en: 'garage', trap: true },
  { w: 'dia', g: 'm', en: 'day', trap: true }, { w: 'mapa', g: 'm', en: 'map', trap: true },
  { w: 'problema', g: 'm', en: 'problem', trap: true }, { w: 'sistema', g: 'm', en: 'system', trap: true },
  { w: 'planeta', g: 'm', en: 'planet', trap: true }, { w: 'idioma', g: 'm', en: 'language', trap: true },
  { w: 'cinema', g: 'm', en: 'movie theater', trap: true }, { w: 'clima', g: 'm', en: 'climate', trap: true },
  { w: 'mão', g: 'f', en: 'hand', trap: true }, { w: 'foto', g: 'f', en: 'photo', trap: true },
  { w: 'tribo', g: 'f', en: 'tribe', trap: true },
];

export function ptGender(n: number): Question[] {
  const traps = PT_NOUNS.filter(x => x.trap);
  const plain = PT_NOUNS.filter(x => !x.trap);
  const qs: Question[] = [];
  for (let k = 0; k < n; k++) {
    const x = Math.random() < 0.45 ? pick(traps) : pick(plain);
    qs.push({
      key: `pt_gender:${x.w}`, topic: 'pt_gender', kind: 'choice', instruction: 'O or a?',
      prompt: `___ ${x.w}`, hint: x.en, options: ['o', 'a'], answers: [x.g === 'm' ? 'o' : 'a'],
      ...(x.trap ? { why: `${x.w} is ${x.g === 'm' ? 'masculine' : 'feminine'}, despite how it looks` } : {}),
    });
  }
  return qs;
}
